/**
 * Opening the capture panel over home (`capture-sheet.ts`) from anywhere, without loading the panel's form: a history
 * entry `/#record`, `/#type` or (v1 ticket 42) `/#type?answer=<id>`. Kept apart so the landing and the moment screen
 * can offer Answer them without pulling the capture form into their chunks.
 */
import { captureHashOf, captureRouteOf, type CaptureRoute, type CaptureTabName } from '../newcomers';
import { mayLeave } from '../router';

/** Where home's URL opens the panel, or null when it is closed. */
export const routeHere = (): CaptureRoute | null => (location.pathname === '/' ? captureRouteOf(location.hash) : null);

/** Recovery always names a draft, so unavailable Listen cannot fall back to a fresh recording. */
export function savedRecordingPath(id: string): string {
  return `/${captureHashOf({ tab: 'listen', draft: id })}`;
}

/** Opens the panel on `tab`, as a history entry on home, so Back closes it; already open, it only changes tab. */
export function openCapture(tab: CaptureTabName, answer?: string) {
  const hash = captureHashOf({ tab, ...(answer ? { answer } : {}) });
  if (routeHere()) history.replaceState({ capture: tab }, '', `/${hash}`);
  else history.pushState({ capture: tab }, '', `/${hash}`);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/**
 * Answer them (v1 ticket 42): the Type panel over home, answering newcomer moment `id`. From another screen (the
 * landing's sign, the moment screen) it asks that screen's leave guard first; Back returns there.
 */
export function openAnswer(id: string) {
  if (location.pathname !== '/' && !mayLeave()) return;
  openCapture('wanted', id);
}

/** Where the lantern glowed when last tapped (viewport pixels), for the panel to bloom out of; taken once. */
let bloom: { x: number; y: number; at: number } | null = null;

/** The lantern was tapped at (x, y): the panel it opens blooms from there. */
export function bloomFrom(x: number, y: number) {
  bloom = { x, y, at: Date.now() };
}

/** The point the panel opening now blooms from, if a lantern was tapped just before; cleared either way. */
export function takeBloom(): { x: number; y: number } | null {
  const b = bloom;
  bloom = null;
  return b && Date.now() - b.at < 1000 ? { x: b.x, y: b.y } : null;
}
